import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { shipmentGroupAPI } from "../../services/api";
import {
  CreateShipmentGroupData,
  UpdateShipmentGroupData,
  AddShipmentsToGroupData,
  RemoveShipmentsFromGroupData,
  ReorderShipmentsData,
  ShipmentGroupState,
} from "../../types";

// Get all shipment groups
export const getShipmentGroups = createAsyncThunk(
  "shipmentGroup/getShipmentGroups",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await shipmentGroupAPI.getShipmentGroups(params);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch shipment groups",
      );
    }
  },
);

// Get single shipment group
export const getShipmentGroup = createAsyncThunk(
  "shipmentGroup/getShipmentGroup",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await shipmentGroupAPI.getShipmentGroup(id);
      return response.data.data.group;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch shipment group",
      );
    }
  },
);

// Create shipment group
export const createShipmentGroup = createAsyncThunk(
  "shipmentGroup/createShipmentGroup",
  async (groupData: CreateShipmentGroupData, { rejectWithValue }) => {
    try {
      const response = await shipmentGroupAPI.createShipmentGroup(groupData);
      return response.data.data.group;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create shipment group",
      );
    }
  },
);

// Update shipment group
export const updateShipmentGroup = createAsyncThunk(
  "shipmentGroup/updateShipmentGroup",
  async (
    { id, groupData }: { id: string; groupData: UpdateShipmentGroupData },
    { rejectWithValue },
  ) => {
    try {
      const response = await shipmentGroupAPI.updateShipmentGroup(
        id,
        groupData,
      );
      return response.data.data.group;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update shipment group",
      );
    }
  },
);

// Add shipments to group
export const addShipmentsToGroup = createAsyncThunk(
  "shipmentGroup/addShipmentsToGroup",
  async (
    { id, data }: { id: string; data: AddShipmentsToGroupData },
    { rejectWithValue },
  ) => {
    try {
      const response = await shipmentGroupAPI.addShipmentsToGroup(id, data);
      return response.data.data.group;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add shipments to group",
      );
    }
  },
);

// Remove shipments from group
export const removeShipmentsFromGroup = createAsyncThunk(
  "shipmentGroup/removeShipmentsFromGroup",
  async (
    { id, data }: { id: string; data: RemoveShipmentsFromGroupData },
    { rejectWithValue },
  ) => {
    try {
      const response = await shipmentGroupAPI.removeShipmentsFromGroup(
        id,
        data,
      );
      return response.data.data.group;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to remove shipments from group",
      );
    }
  },
);

// Reorder shipments in group
export const reorderShipments = createAsyncThunk(
  "shipmentGroup/reorderShipments",
  async (
    { id, data }: { id: string; data: ReorderShipmentsData },
    { rejectWithValue },
  ) => {
    try {
      const response = await shipmentGroupAPI.reorderShipments(id, data);
      return response.data.data.group;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reorder shipments",
      );
    }
  },
);

// Delete shipment group
export const deleteShipmentGroup = createAsyncThunk(
  "shipmentGroup/deleteShipmentGroup",
  async (id: string, { rejectWithValue }) => {
    try {
      await shipmentGroupAPI.deleteShipmentGroup(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete shipment group",
      );
    }
  },
);

// Delete multiple shipment groups
export const deleteMultipleShipmentGroups = createAsyncThunk(
  "shipmentGroup/deleteMultipleShipmentGroups",
  async (ids: string[], { rejectWithValue }) => {
    try {
      await shipmentGroupAPI.deleteMultipleShipmentGroups(ids);
      return ids;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete shipment groups",
      );
    }
  },
);

export const assignGroupToDriverApp = createAsyncThunk(
  "shipmentGroup/assignGroupToDriverApp",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await shipmentGroupAPI.assignGroupToDriverApp(id);
      return { id, assignedCount: response.data.data.assignedCount };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to assign group to driver app",
      );
    }
  },
);

export const removeGroupFromDriverApp = createAsyncThunk(
  "shipmentGroup/removeGroupFromDriverApp",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await shipmentGroupAPI.removeGroupFromDriverApp(id);
      return { id, removedCount: response.data.data.removedCount };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to remove group from driver app",
      );
    }
  },
);

const initialState: ShipmentGroupState = {
  groups: [],
  currentGroup: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },
};

const shipmentGroupSlice = createSlice({
  name: "shipmentGroup",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentGroup: (state) => {
      state.currentGroup = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    resetShipmentGroupState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Get Shipment Groups
      .addCase(getShipmentGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getShipmentGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload.groups;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getShipmentGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Get Shipment Group
      .addCase(getShipmentGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getShipmentGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.currentGroup = action.payload;
        state.error = null;
      })
      .addCase(getShipmentGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create Shipment Group
      .addCase(createShipmentGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createShipmentGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups.unshift(action.payload);
        state.pagination.total += 1;
        state.currentGroup = action.payload;
        state.error = null;
      })
      .addCase(createShipmentGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Shipment Group
      .addCase(updateShipmentGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateShipmentGroup.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(
          (group) => group._id === action.payload._id,
        );
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        state.currentGroup = action.payload;
        state.error = null;
      })
      .addCase(updateShipmentGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Add Shipments to Group
      .addCase(addShipmentsToGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addShipmentsToGroup.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(
          (group) => group._id === action.payload._id,
        );
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        state.currentGroup = action.payload;
        state.error = null;
      })
      .addCase(addShipmentsToGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Remove Shipments from Group
      .addCase(removeShipmentsFromGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeShipmentsFromGroup.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(
          (group) => group._id === action.payload._id,
        );
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        state.currentGroup = action.payload;
        state.error = null;
      })
      .addCase(removeShipmentsFromGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Reorder Shipments
      .addCase(reorderShipments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reorderShipments.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(
          (group) => group._id === action.payload._id,
        );
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        state.currentGroup = action.payload;
        state.error = null;
      })
      .addCase(reorderShipments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Shipment Group
      .addCase(deleteShipmentGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteShipmentGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = state.groups.filter(
          (group) => group._id !== action.payload,
        );
        state.pagination.total -= 1;
        if (state.currentGroup?._id === action.payload) {
          state.currentGroup = null;
        }
        state.error = null;
      })
      .addCase(deleteShipmentGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Multiple Shipment Groups
      .addCase(deleteMultipleShipmentGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMultipleShipmentGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = state.groups.filter(
          (group) => !action.payload.includes(group._id),
        );
        state.pagination.total -= action.payload.length;
        if (
          state.currentGroup &&
          action.payload.includes(state.currentGroup._id)
        ) {
          state.currentGroup = null;
        }
        state.error = null;
      })
      .addCase(deleteMultipleShipmentGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(assignGroupToDriverApp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignGroupToDriverApp.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(assignGroupToDriverApp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Remove Group from Driver App
      .addCase(removeGroupFromDriverApp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeGroupFromDriverApp.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(removeGroupFromDriverApp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentGroup,
  setLoading,
  resetShipmentGroupState,
} = shipmentGroupSlice.actions;

export default shipmentGroupSlice.reducer;
